import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { usePullToRefresh } from '../../utils/pullToRefresh/usePullToRefresh';
import { PullToRefreshIndicator } from '../../utils/pullToRefresh/PullToRefreshIndicator';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area, ReferenceLine, LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBranch } from "../../context/BranchContext";
import { getCurrencySymbol } from "../../utils/currency";
import { escapeCsvCell, CSV_BOM } from "../../utils/reportExport";
import { isNativePlatform, exportTablePdfNative, exportTableXlsxNative } from "../../utils/capacitorExport";
import { useDesktopTopBar } from "../../context/DesktopLayoutContext";
import { useDrillHistoryBack } from "../../hooks/useDrillHistoryBack";
import DesktopLayout from "../layout/DesktopLayout";
import TopBar from "../layout/TopBar";
import Phone from "../Phone";
import BottomNav from "../BottomNav";
import { useSharedReportDateFilter } from "../../redux/reportFilters/reportFiltersHooks";
import { exportPeriodFor } from "../../utils/reportPeriod";
import "./reports.css";
import { T, useIsDesktop, Bone } from "./reportTokens";
import SectionError from "./SectionError";
import SectionEmpty from "./SectionEmpty";
import { ReportSectionExportButton } from "./components/ReportExportButton";
import { useSwipeBack } from '../../hooks/useSwipeBack';
// Icon and OutletDropdown available via TopBar/DesktopLayout
import {
    getEventOverview,
    getEventPreOrderByOrderDate,
    getEventPreOrderChannels,
    getEventNameFilter,
    getEventItemSales,
    getEventPreOrderByPickup,
    getEventPreOrderDetails,
    todayStr,
    yesterdayStr,
} from "../../services/reportsApi";
import { useStoreTimeZone, getStoreTimeZone, getYesterdayRange, get7DayRange, get30DayRange, getThisMonthRange } from '../../utils/dateTimeUtils';

function EventDesktopShimmer() {
    const overviewLabels = ["Total Sales", "Total Orders", /* "Avg Order Value", */ "Total Ordered Items", "Cancelled Orders", "Refund"];
    return (
        <div className="rpt-page">
            {/* 1. Order overview cards (no section title) */}
            <div className="rpt-flex-row rpt-flex-wrap rpt-gap-16" style={{ marginBottom: 16 }}>
                {overviewLabels.map((label) => (
                    <div key={label} className="rpt-tile" style={{ flex: "1 1 155px", minWidth: 180, background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 16, padding: "20px 22px", boxShadow: T.cardShadow }}>
                        <div className="rpt-tile__label" style={{ marginBottom: 8 }}>{label}</div>
                        <Bone h={32} r={6} />
                    </div>
                ))}
            </div>
            {/* 2. Order Details table */}
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
                <div className="rpt-section-title" style={{ marginBottom: 12 }}>Order Details</div>
                {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-gap-12" style={{ marginBottom: 10 }}>
                        <Bone w={60} h={14} r={4} />
                        <Bone w={120} h={14} r={4} />
                        <Bone w={80} h={14} r={4} />
                        <Bone w={100} h={14} r={4} />
                    </div>
                ))}
            </div>
            {/* 3. Pickup by slot (wide) + By Channel (narrow) — same row */}
            <div className="rpt-flex-row rpt-gap-16 rpt-row-equal-height" style={{ flexWrap: "wrap" }}>
                <div className="rpt-row-cell" style={{ flex: "2 1 480px", minWidth: 320, minHeight: 0 }}>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, height: "100%", display: "flex", flexDirection: "column" }}>
                        <div className="rpt-section-title" style={{ marginBottom: 12 }}>Pickup by slot</div>
                        <div style={{ flex: 1, minHeight: 120 }}><Bone h={200} r={8} /></div>
                    </div>
                </div>
                <div className="rpt-row-cell" style={{ flex: "1 1 280px", minWidth: 260, minHeight: 0 }}>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, height: "100%", display: "flex", flexDirection: "column" }}>
                        <div className="rpt-section-title" style={{ marginBottom: 12 }}>By Channel</div>
                        <div style={{ flex: 1, minHeight: 100 }}><Bone h={140} r={8} /></div>
                    </div>
                </div>
            </div>
            {/* 4. Item Sales (wide) + Prep Quantity List (narrow) — same row */}
            <div className="rpt-flex-row rpt-gap-16 rpt-row-equal-height" style={{ flexWrap: "wrap" }}>
                <div className="rpt-row-cell" style={{ flex: "2 1 480px", minWidth: 320, minHeight: 0 }}>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, height: "100%", display: "flex", flexDirection: "column" }}>
                        <div className="rpt-section-title" style={{ marginBottom: 12 }}>Item Sales</div>
                        <div style={{ flex: 1, minHeight: 120 }}>
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="rpt-flex-row rpt-flex-between" style={{ marginBottom: 8 }}>
                                    <Bone w={140} h={14} r={4} />
                                    <Bone w={40} h={14} r={4} />
                                    <Bone w={50} h={14} r={4} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="rpt-row-cell" style={{ flex: "1 1 280px", minWidth: 260, minHeight: 0 }}>
                    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow, height: "100%", display: "flex", flexDirection: "column" }}>
                        <div className="rpt-section-title" style={{ marginBottom: 12 }}>Prep Quantity List</div>
                        <div style={{ flex: 1, minHeight: 100 }}>
                            {[1, 2, 3, 4].map((i) => (
                                <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-10" style={{ marginBottom: 8 }}>
                                    <Bone w={140} h={14} r={4} />
                                    <Bone w={80} h={12} r={4} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            {/* 5. Customer Run Sheet */}
            <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 14, padding: 22, boxShadow: T.cardShadow }}>
                <div className="rpt-section-title" style={{ marginBottom: 12 }}>Customer Run Sheet</div>
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-gap-12" style={{ marginBottom: 10 }}>
                        <Bone w={120} h={14} r={4} />
                        <Bone w={80} h={14} r={4} />
                        <Bone w={60} h={14} r={4} />
                        <Bone w={60} h={14} r={4} />
                    </div>
                ))}
            </div>
        </div>
    );
}

function EventMobileShimmer() {
    const overviewLabels = ["Total Sales", "Total Orders", /* "Avg Order Value", */ "Total Ordered Items", "Cancelled Orders", "Refund"];
    return (
        <div className="rpt-flex-col rpt-gap-8" style={{ padding: "8px 16px 16px" }}>
            {/* 1. Order overview cards (2 rows of 3) */}
            {[overviewLabels.slice(0, 3), overviewLabels.slice(3, 6)].map((row, ri) => (
                <div key={ri} style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                    {row.map((label) => (
                        <div key={label} style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 12, padding: "12px 10px", textAlign: "center", boxShadow: T.shadow }}>
                            <div className="rpt-card-label-sm" style={{ color: T.t3, marginBottom: 4 }}>{label}</div>
                            <Bone h={18} r={5} />
                        </div>
                    ))}
                </div>
            ))}
            {/* 2. Order Details */}
            <div className="rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Order Details</div>
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-gap-8" style={{ marginBottom: 6 }}>
                        <Bone w={50} h={12} r={4} />
                        <Bone w={80} h={12} r={4} />
                        <Bone w={60} h={12} r={4} />
                    </div>
                ))}
            </div>
            {/* 3. Pickup by slot */}
            <div className="rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Pickup by slot</div>
                <Bone h={100} r={8} />
            </div>
            {/* 4. Item Sales */}
            <div className="rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Item Sales</div>
                {[1, 2].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-flex-between" style={{ marginBottom: 6 }}>
                        <Bone w={100} h={12} r={4} />
                        <Bone w={30} h={12} r={4} />
                    </div>
                ))}
            </div>
            {/* 5. By Channel */}
            <div className="rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">By Channel</div>
                <Bone h={100} r={8} />
            </div>
            {/* 6. Prep Quantity List */}
            <div className="rpt-card" style={{ padding: 14 }}>
                <div className="rpt-section-title">Prep Quantity List</div>
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: 8 }}>
                        <Bone w={110} h={12} r={4} />
                        <Bone w={40} h={12} r={4} />
                    </div>
                ))}
            </div>
        </div>
    );
}

/* =============================================================================
   EMPTY DEFAULTS (shown when API returns no data)
   ============================================================================= */
const EMPTY_DATA = {
    kpis: {
        totalRevenue: 0, totalOrders: 0, avgOrderValue: 0,
        fulfillmentRate: 0, noShows: 0, cancellations: 0,
        revenueDelta: null, ordersDelta: null, aovDelta: null,
        fulfillmentDelta: null, noShowsDelta: null, cancellationsDelta: null,
    },
    eventName: "",
    eventDates: "",
    itemQuantities: [],
    pickupSlots: [],
    bookingCurve: [],
    channelMix: [],
    fulfillmentStatus: [],
    customerRunSheet: [],
    // Portal-aligned KPIs: order overview stamps, order details table
    orderOverview: {
        totalPreOrders: 0,
        cancelledPreOrders: 0,
        totalPreOrderQty: 0,
        totalPreOrderSales: 0,
        cancelledPreOrderSales: 0,
    },
    orderDetails: { content: [], totalPages: 0, totalElements: 0 },
    itemSales: { content: [], totalPages: 0, totalElements: 0 },
    prepQuantity: { content: [], totalPages: 0, totalElements: 0 },
};

/* =============================================================================
   HOOKS & UTILITIES
   ============================================================================= */
const findToken = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["accessToken", "access_token", "token", "jwtToken", "jwt", "idToken", "id_token"]) {
        if (obj[key] && typeof obj[key] === "string" && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === "string" && val.length > 50) return val;
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth: any = 0) => {
    if (!obj || typeof obj !== "object" || depth > 3) return null;
    for (const key of ["locationId", "location_id", "locationID", "locId", "outletId"]) {
        if (obj[key] && typeof obj[key] === "string") return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === "object" && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const num = (v: any) => Number(v ?? 0) || 0;
const fmtCurrency = (v: any, decimals = 0) =>
    getCurrencySymbol() + num(v).toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    });const fmtNum = (v: any) => num(v).toLocaleString("en-US");

// Historical presets are anchored on the store-timezone anchor date (5 AM cutoff),
// not the raw `yesterday` arg — see getAnchorDate / DateProdIssue.md.
const getStartEndFromFilter = (filter: any, today: any, yesterday: any, timeZoneCd: string = getStoreTimeZone()) => {
    const raw = typeof filter === "string" ? filter.trim() : "";
    const normalized = raw.toLowerCase().replace(/\s+/g, "");
    if (raw && raw.startsWith("custom:")) {
        const p = filter.split(":"); if (p.length === 3) return { startDate: p[1], endDate: p[2] };
    }
    if (normalized === "today") return { startDate: today, endDate: today };
    if (normalized === "yesterday") return getYesterdayRange(timeZoneCd);
    if (normalized === "7d") return get7DayRange(timeZoneCd);
    if (normalized === "30d") return get30DayRange(timeZoneCd);
    if (normalized === "thismonth") return getThisMonthRange(timeZoneCd);
    return null;
};

/** Format YYYY-MM-DD as "Mon DD, YYYY" for display */
const fmtEventDate = (str: any) => {
    if (!str) return "";
    try {
        const d = new Date(str + "T12:00:00");
        const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
        return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    } catch {
        return str;
    }
};

/* =============================================================================
   EVENT SELECTOR DROPDOWN (custom styled)
   ============================================================================= */
function EventSelector({ eventNames, value, onChange, loading, disabled, mobile }: any) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<any>(null);

    const options = useMemo(() => {
        const list = Array.isArray(eventNames) ? eventNames : [];
        return list.map((e) => {
            const slug = e.eventSlug ?? e.slug ?? e.id ?? "";
            const name = e.eventName ?? e.name ?? "Unnamed event";
            const start = e.startDate ? fmtEventDate(e.startDate) : "";
            const end = e.endDate ? fmtEventDate(e.endDate) : "";
            const label = start && end ? `${name} (${start} – ${end})` : start ? `${name} (${start})` : name;
            return { value: slug, label, raw: e };
        });
    }, [eventNames]);

    const selectedValue = value || (options[0]?.value ?? "");
    const selectedOption = options.find((o) => o.value === selectedValue);
    const displayLabel = selectedOption?.label ?? (options.length === 0 ? (loading ? "Loading events…" : "No events") : "Select event");
    const isInteractive = options.length > 0 && !disabled && !loading;

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e: any) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    return (
        <div style={{
            display: "flex",
            alignItems: "center",
            gap: mobile ? 8 : 10,
            padding: mobile ? "8px 16px" : "4px 0",
            background: "transparent",
            flexWrap: "wrap",
            width: mobile ? "100%" : "fit-content",
            maxWidth: "100%",
            boxSizing: "border-box",
        }}>
            <label style={{ fontSize: mobile ? 12 : 13, fontWeight: 600, color: T.t2, whiteSpace: "nowrap" }}>
                Event
            </label>
            <div ref={containerRef} style={{ position: "relative", flex: mobile ? "1 1 200px" : "none", width: mobile ? undefined : 380, minWidth: mobile ? 0 : 280, maxWidth: mobile ? "100%" : 420 }}>
                <button
                    type="button"
                    onClick={() => isInteractive && setOpen((o) => !o)}
                    disabled={!isInteractive}
                    style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        padding: mobile ? "8px 12px" : "8px 14px",
                        fontSize: mobile ? 12 : 13,
                        fontWeight: 500,
                        color: options.length ? T.t1 : T.t3,
                        background: T.card,
                        border: `1px solid ${T.cardBorder}`,
                        borderRadius: 10,
                        cursor: isInteractive ? "pointer" : "not-allowed",
                        fontFamily: T.font,
                        textAlign: "left",
                        outline: "none",
                    }}
                >
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{displayLabel}</span>
                    {loading ? (
                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3, flexShrink: 0 }}>
                            progress_activity
                        </span>
                    ) : (
                        <span className="material-symbols-rounded" style={{
                            fontSize: 18,
                            color: T.t3,
                            flexShrink: 0,
                            transform: `rotate(${open ? "180deg" : "0deg"})`,
                            transition: "transform 0.2s ease",
                        }}>
                            expand_more
                        </span>
                    )}
                </button>
                {open && options.length > 0 && (
                    <div style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        background: T.surface,
                        border: `1px solid ${T.cardBorder}`,
                        borderRadius: 10,
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                        maxHeight: 260,
                        overflowY: "auto",
                        zIndex: 100,
                    }}>
                        {options.map((opt) => {
                            const isSelected = opt.value === selectedValue;
                            return (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                        onChange(opt.value);
                                        setOpen(false);
                                    }}
                                    style={{
                                        width: "100%",
                                        display: "block",
                                        padding: "10px 14px",
                                        fontSize: 12,
                                        fontWeight: isSelected ? 600 : 500,
                                        color: isSelected ? T.accent : T.t1,
                                        background: isSelected ? T.accentSoft : "transparent",
                                        border: "none",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        fontFamily: T.font,
                                        outline: "none",
                                        transition: "background 0.15s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.background = T.hover;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = isSelected ? T.accentSoft : "transparent";
                                    }}
                                >
                                    {opt.label}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

/* =============================================================================
   DATA HOOK
   ============================================================================= */

// Unwrap API response — responses may be wrapped in metaDataInfo.data, content, data, etc.
const unwrap = (res: any) => {
    if (res == null) return null;
    const d = res?.metaDataInfo?.data ?? res?.content ?? res?.data ?? res?.result ?? res?.response;
    return (d != null && d !== res) ? d : res;
};

// Extract an array from an API response (try many common wrapper keys)
const listFrom = (res: any) => {
    const r = unwrap(res);
    if (Array.isArray(r)) return r;
    if (!r || typeof r !== "object") return [];
    const arr = r.content ?? r.data ?? r.items ?? r.records ?? r.orders ?? r.list ?? r.results;
    if (Array.isArray(arr)) return arr;
    for (const val of Object.values(r)) {
        if (Array.isArray(val)) return val;
    }
    return [];
};

// Deep pick numeric value (search nested objects)
const pickNum = (obj: any, keys: any) => {
    if (!obj || typeof obj !== "object") return 0;
    for (const key of keys) {
        const v = obj[key];
        if (v != null && v !== "" && !Number.isNaN(Number(v))) return Number(v);
    }
    for (const v of Object.values(obj)) {
        if (v && typeof v === "object" && !Array.isArray(v)) {
            const nested = pickNum(v, keys);
            if (nested > 0) return nested;
        }
    }
    return 0;
};

// Deep pick string value
const pickStr = (obj: any, keys: any, fallback: any = "") => {
    if (!obj || typeof obj !== "object") return fallback;
    for (const key of keys) {
        const v = obj[key];
        if (v != null && typeof v === "string" && v.trim()) return v;
    }
    for (const v of Object.values(obj)) {
        if (v && typeof v === "object" && !Array.isArray(v)) {
            const nested = pickStr(v, keys, "");
            if (nested) return nested;
        }
    }
    return fallback;
};

function useEventData(locationId: any, token: any, dateRange: any, selectedEventSlug: any, pages: any = {}, eventDateOverride: any, pickupSlotDateOverride: any) {
    const [data, setData] = useState(EMPTY_DATA);
    const [loading, setLoading] = useState(true);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [pickupSlotLoading, setPickupSlotLoading] = useState(false);
    const [error, setError] = useState("");
    const [eventNames, setEventNames] = useState([]);
    const [eventNamesLoading, setEventNamesLoading] = useState(true);
    const orderDetailsPage = pages.orderDetailsPage ?? 1;
    const itemSalesPage = pages.itemSalesPage ?? 1;
    const prepPage = pages.prepPage ?? 1;
    const [prepLoading, setPrepLoading] = useState(false);
    const [orderDetailsError, setOrderDetailsError] = useState(false);
    const [pickupSlotsError, setPickupSlotsError] = useState(false);
    const [orderDetailsRetryTick, setOrderDetailsRetryTick] = useState(0);
    const [pickupSlotsRetryTick, setPickupSlotsRetryTick] = useState(0);
    const [overviewRetryTick, setOverviewRetryTick] = useState(0);
    const [prepRetryTick, setPrepRetryTick] = useState(0);
    const retryOrderDetails = () => setOrderDetailsRetryTick((n) => n + 1);
    const retryPickupSlots = () => setPickupSlotsRetryTick((n) => n + 1);
    const retryOverview = () => setOverviewRetryTick((n) => n + 1);
    const retryPrep = () => setPrepRetryTick((n) => n + 1);

    // Phase 1: Fetch event names list — same as portal: only locationId (no date filter)
    // GET .../events/event-name?locationId=... returns all events for the location
    useEffect(() => {
        if (!locationId || !token) { setEventNamesLoading(false); return; }
        setEventNamesLoading(true);
        getEventNameFilter({ locationId, token })
            .then((res) => {
                const raw = unwrap(res);
                const list = listFrom(raw);
                setEventNames(list);
            })
            .catch(() => setEventNames([]))
            .finally(() => setEventNamesLoading(false));
    }, [locationId, token]);

    // Determine the eventSlug to use: explicit selection > first from list
    const eventSlug = selectedEventSlug
        || (eventNames.length > 0 ? (eventNames[0]?.eventSlug ?? eventNames[0]?.slug ?? eventNames[0]?.id ?? "") : "");

    // Phase 2a: Fetch overview + charts (only when event/location changes — NOT on page/date changes)
    useEffect(() => {
        if (!locationId || !token) { setLoading(false); return; }
        if (!eventSlug) {
            // Still fetching event names — wait for that to finish before deciding
            if (eventNames.length === 0 && eventNamesLoading) return;
            // No events exist for this location — stop loading, show empty state
            setData(EMPTY_DATA);
            setLoading(false);
            return;
        }

        const selectedEvt = Array.isArray(eventNames) ? eventNames.find(e => (e.eventSlug ?? e.slug ?? e.id) === eventSlug) : null;
        const fallbackDate = selectedEvt?.startDate || dateRange.startDate;
        const opts = { locationId, eventSlug, token };
        const optsWithEventDate = { ...opts, eventDate: fallbackDate };

        setLoading(true);
        setError("");

        Promise.allSettled([
            getEventOverview(optsWithEventDate),
            getEventPreOrderByOrderDate(optsWithEventDate),
            getEventPreOrderChannels(optsWithEventDate),
        ]).then(([overview, orderDate, channels]) => {
            const ovRaw = overview.status === "fulfilled" ? overview.value : null;
            const odRaw = orderDate.status === "fulfilled" ? orderDate.value : null;
            const chRaw = channels.status === "fulfilled" ? channels.value : null;

            const ov = unwrap(ovRaw);
            const ch = unwrap(chRaw);

            const revenue = pickNum(ov, ["totalPreOrderSales", "totalRevenue", "revenue", "eventRevenue", "netSales", "grossSales", "totalSales"]);
            const orders = pickNum(ov, ["totalPreOrders", "totalOrders", "orders", "orderCount", "totalOrder"]);
            const hasData = revenue > 0 || orders > 0 || (ov && typeof ov === "object" && Object.keys(ov).length > 0);

            if (!hasData) {
                setData(EMPTY_DATA);
            } else {
                const kpis = {
                    totalRevenue: revenue,
                    totalOrders: orders,
                    avgOrderValue: pickNum(ov, ["avgOrderValue", "aov", "averageOrderValue", "avgOrder"]),
                    fulfillmentRate: pickNum(ov, ["fulfillmentRate", "fulfillment", "fulfillmentPct"]),
                    noShows: pickNum(ov, ["noShows", "noShowCount", "noShow"]),
                    cancellations: pickNum(ov, ["cancellations", "cancelCount", "cancelled", "cancelledCount"]),
                    revenueDelta: ov?.revenueDelta ?? null,
                    ordersDelta: ov?.ordersDelta ?? null,
                    aovDelta: ov?.aovDelta ?? null,
                    fulfillmentDelta: ov?.fulfillmentDelta ?? null,
                    noShowsDelta: ov?.noShowsDelta ?? null,
                    cancellationsDelta: ov?.cancellationsDelta ?? null,
                };

                const en = eventNames;
                const selectedEvtInner = Array.isArray(en) ? en.find(e => (e.eventSlug ?? e.slug ?? e.id) === eventSlug) : null;
                const eventName = selectedEvtInner?.eventName ?? selectedEvtInner?.name ?? (pickStr(ov, ["eventName", "name"]) || EMPTY_DATA.eventName);
                const eventDatesVal = selectedEvtInner?.eventDates ?? selectedEvtInner?.dates ?? (pickStr(ov, ["eventDates", "dates"]) || EMPTY_DATA.eventDates);

                const odList = listFrom(odRaw);
                const mappedBooking = odList.map(d => ({
                    day: d.day ?? d.label ?? d.date ?? d.orderDate ?? "—",
                    current: pickNum(d, ["current", "orders", "orderCount", "count"]),
                    prior: pickNum(d, ["prior", "previousYear", "lastYear", "compare"]),
                }));

                const chList = listFrom(chRaw);
                const channelColors = ["#D23A3B", "#D97706", "#16A34A", "#64748B"];
                const totalChannelSales = chList.reduce((s, c) => s + pickNum(c, ["sales", "amount", "value"]), 0);
                const mappedChannels = chList.map((c, i) => {
                    const sales = pickNum(c, ["sales", "amount", "value"]);
                    const pct = totalChannelSales > 0 ? Math.round((sales / totalChannelSales) * 100) : 0;
                    return {
                        name: c.channelName ?? c.name ?? c.channel ?? c.label ?? "—",
                        value: pct,
                        color: channelColors[i % channelColors.length],
                    };
                });

                const orderOverview = {
                    totalPreOrders: pickNum(ov, ["totalPreOrders", "totalOrders", "orders"]),
                    cancelledPreOrders: pickNum(ov, ["cancelledPreOrders", "cancelledOrders", "cancellations"]),
                    totalPreOrderQty: pickNum(ov, ["totalPreOrderQty", "totalPreOrderItems", "totalOrderQty", "totalItems"]),
                    totalPreOrderSales: pickNum(ov, ["totalPreOrderSales", "totalRevenue", "revenue", "totalSales", "grossSales"]),
                    cancelledPreOrderSales: pickNum(ov, ["cancelledPreOrderSales", "refundedSales", "cancelledSales"]),
                };

                setData(prev => ({
                    ...prev,
                    kpis,
                    eventName,
                    eventDates: eventDatesVal,
                    bookingCurve: mappedBooking,
                    channelMix: mappedChannels,
                    fulfillmentStatus: [],
                    customerRunSheet: [],
                    orderOverview,
                }));
            }
            setLoading(false);
        }).catch(err => {
            console.error("Event API error:", err);
            setError(err?.message || "Failed to load event data");
            setData(EMPTY_DATA);
            setLoading(false);
        });
    }, [locationId, token, eventSlug, eventNames, eventNamesLoading, overviewRetryTick]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 2b: Fetch table data (order details + item sales) — grid-only shimmer on page/date changes
    useEffect(() => {
        if (!locationId || !token || !eventSlug) return;

        const selectedEvt = Array.isArray(eventNames) ? eventNames.find(e => (e.eventSlug ?? e.slug ?? e.id) === eventSlug) : null;
        const eventStartDate = eventDateOverride || selectedEvt?.startDate || dateRange.startDate;
        const opts = { locationId, eventSlug, token };

        setPaginationLoading(true);
        setOrderDetailsError(false);

        Promise.allSettled([
            getEventItemSales({ ...opts, size: 5, search: "", page: Math.max(0, itemSalesPage - 1) }),
            getEventPreOrderDetails({ ...opts, pickUpDate: eventStartDate, size: 10, search: "", page: Math.max(0, orderDetailsPage - 1) }),
        ]).then(([itemSales, details]) => {
            const itRaw = itemSales.status === "fulfilled" ? itemSales.value : null;
            const detailsRaw = details.status === "fulfilled" ? details.value : null;

            const detailsRes = detailsRaw && typeof detailsRaw === "object" ? detailsRaw : { content: [], totalPages: 0, totalElements: 0 };
            const detailsContent = Array.isArray(detailsRes?.content) ? detailsRes.content : listFrom(detailsRaw);
            const detailsTotalPages = detailsRes?.totalPages ?? 0;
            const detailsTotalElements = detailsRes?.totalElements ?? detailsContent.length;

            const itemSalesRes = itRaw && typeof itRaw === "object" ? itRaw : { content: [], totalPages: 0, totalElements: 0 };
            const itemSalesContent = Array.isArray(itemSalesRes?.content) ? itemSalesRes.content : listFrom(itRaw);
            const itemSalesTotalPages = itemSalesRes?.totalPages ?? 0;
            const itemSalesTotalElements = itemSalesRes?.totalElements ?? itemSalesContent.length;

            const mappedItems = itemSalesContent.map(i => ({
                name: i.itemName ?? i.name ?? i.menuItemName ?? i.label ?? "—",
                qty: pickNum(i, ["saleQuantity", "quantity", "qty", "salesQty", "itemCount", "count"]),
                revenue: pickNum(i, ["revenue", "totalRevenue", "grossSales", "itemTotal", "totalSales", "amount"]),
            }));

            setData(prev => ({
                ...prev,
                itemQuantities: mappedItems,
                orderDetails: {
                    content: detailsContent,
                    totalPages: detailsTotalPages,
                    totalElements: detailsTotalElements,
                },
                itemSales: {
                    content: mappedItems,
                    totalPages: itemSalesTotalPages,
                    totalElements: itemSalesTotalElements,
                },
            }));
            // Both halves of the batch errored → section failed
            setOrderDetailsError(itemSales.status === "rejected" && details.status === "rejected");
            setPaginationLoading(false);
        }).catch(() => {
            setOrderDetailsError(true);
            setPaginationLoading(false);
        });
    }, [locationId, token, eventSlug, eventNames, orderDetailsPage, itemSalesPage, eventDateOverride, orderDetailsRetryTick]);

    // Phase 2c: Fetch prep quantity list (size 10, separate pagination)
    useEffect(() => {
        if (!locationId || !token || !eventSlug) return;

        const opts = { locationId, eventSlug, token };
        setPrepLoading(true);

        getEventItemSales({ ...opts, size: 10, search: "", page: Math.max(0, prepPage - 1) })
            .then((res) => {
                const raw = res && typeof res === "object" ? res : { content: [], totalPages: 0, totalElements: 0 };
                const content = Array.isArray(raw?.content) ? raw.content : listFrom(res);
                const mapped = content.map(i => ({
                    name: i.itemName ?? i.name ?? i.menuItemName ?? i.label ?? "—",
                    qty: pickNum(i, ["saleQuantity", "quantity", "qty", "salesQty", "itemCount", "count"]),
                    revenue: pickNum(i, ["revenue", "totalRevenue", "grossSales", "itemTotal", "totalSales", "amount"]),
                }));
                setData(prev => ({
                    ...prev,
                    prepQuantity: {
                        content: mapped,
                        totalPages: raw?.totalPages ?? 0,
                        totalElements: raw?.totalElements ?? mapped.length,
                    },
                }));
            })
            .catch(() => {
                setData(prev => ({ ...prev, prepQuantity: { content: [], totalPages: 0, totalElements: 0 } }));
            })
            .finally(() => setPrepLoading(false));
    }, [locationId, token, eventSlug, prepPage, prepRetryTick]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 2e: Fetch pickup by slot — re-fires when the Pickup by slot date picker changes
    useEffect(() => {
        if (!locationId || !token || !eventSlug) return;

        const selectedEvt = Array.isArray(eventNames) ? eventNames.find(e => (e.eventSlug ?? e.slug ?? e.id) === eventSlug) : null;
        const fallbackDate = selectedEvt?.startDate || dateRange.startDate;
        const pickupDate = pickupSlotDateOverride || fallbackDate;

        console.log('[EventReport] getEventPreOrderByPickup params:', { locationId, eventSlug, eventDate: pickupDate });

        setPickupSlotLoading(true);
        setPickupSlotsError(false);

        getEventPreOrderByPickup({ locationId, eventSlug, eventDate: pickupDate, token })
            .then((res) => {
                console.log('[EventReport] getEventPreOrderByPickup (/events/pre-order/pickup):', JSON.stringify(res, null, 2));
                const pickupList = listFrom(res);
                const pickupSlotsMapped = pickupList.map((x) => ({
                    slot: x.pickupSlot ?? x.slot ?? x.timeSlot ?? "—",
                    orders: pickNum(x, ["orderCount", "orders", "count"]),
                    sales: pickNum(x, ["sales", "revenue", "totalSales"]),
                    itemQty: pickNum(x, ["itemQty", "itemQuantity", "qty"]),
                }));
                setData(prev => ({ ...prev, pickupSlots: pickupSlotsMapped }));
                setPickupSlotsError(false);
            })
            .catch(() => { setData(prev => ({ ...prev, pickupSlots: [] })); setPickupSlotsError(true); })
            .finally(() => setPickupSlotLoading(false));
    }, [locationId, token, eventSlug, pickupSlotDateOverride, eventNames, dateRange.startDate, pickupSlotsRetryTick]);

    return { data, loading, paginationLoading, pickupSlotLoading, prepLoading, error, eventNames, eventNamesLoading, eventSlug, orderDetailsError, pickupSlotsError, retryOrderDetails, retryPickupSlots, retryOverview, retryPrep };
}

/* =============================================================================
   DESIGN SYSTEM PRIMITIVES
   ============================================================================= */

function Card({ children, style, drillable, mobile, onClick }: any) {
    return (
        <div
            onClick={onClick}
            style={{
                background: T.card,
                border: `1px solid ${drillable ? "rgba(210,58,59,0.2)" : T.cardBorder}`,
                borderRadius: 14,
                padding: mobile ? 14 : "22px 60px 22px 22px",
                boxShadow: mobile ? T.shadow : T.cardShadow,
                position: "relative",
                cursor: onClick ? "pointer" : undefined,
                ...style,
            }}
        >
            {drillable && (
                <svg width="10" height="10" viewBox="0 0 10 10" style={{ position: "absolute", top: 8, right: 8, opacity: 0.45 }}>
                    <path d="M3.5 2L7 5L3.5 8" fill="none" stroke={T.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
            {children}
        </div>
    );
}

function SecTitle({ children, sub, mobile, right }: any) {
    return (
        <div style={{ marginBottom: mobile ? 8 : 12, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
                <div style={{ fontSize: mobile ? 12 : 14, fontWeight: 600, color: T.t1 }}>{children}</div>
                {sub && <div style={{ fontSize: mobile ? 9 : 11, fontWeight: 400, color: T.t3, marginTop: 2 }}>{sub}</div>}
            </div>
            {right}
        </div>
    );
}

function MTooltip({ active, payload, label }: any) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 12px", boxShadow: T.cardShadow, fontFamily: T.font }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
            {payload.map((p, i) => (
                <div key={i} style={{ fontSize: 10, color: p.color || T.t2, marginTop: 2 }}>
                    {p.name}: <span style={{ fontWeight: 600, color: T.t1 }}>{p.value}</span>
                </div>
            ))}
        </div>
    );
}

function StatusPill({ status }: any) {
    const map = {
        "Fully Paid": { bg: "rgba(22,163,74,0.08)", color: "#16A34A" },
        "Deposit Paid": { bg: "rgba(217,119,6,0.08)", color: "#D97706" },
        "Unpaid": { bg: "rgba(220,38,38,0.08)", color: "#DC2626" },
        "Picked Up": { bg: "rgba(22,163,74,0.08)", color: "#16A34A" },
        "Ready": { bg: "rgba(210,58,59,0.08)", color: "#D23A3B" },
        "Preparing": { bg: "rgba(217,119,6,0.08)", color: "#D97706" },
        "Ordered": { bg: "#F0F1F5", color: "#475569" },
        "No-Show": { bg: "rgba(220,38,38,0.08)", color: "#DC2626" },
        "Cancelled": { bg: "#F0F1F5", color: "#64748B" },
    };
    const s = map[status] || { bg: "#F0F1F5", color: "#64748B" };
    return <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 4, background: s.bg, color: s.color, whiteSpace: "nowrap" }}>{status}</span>;
}

function TablePager({ page = 1, totalPages = 1, onPageChange, mobile }: any) {
    const safeTotal = Math.max(1, Number(totalPages) || 1);
    const safePage = Math.min(Math.max(1, Number(page) || 1), safeTotal);
    return (
        <div className="rpt-flex-row rpt-flex-center rpt-gap-8" style={{ justifyContent: "flex-end", marginTop: 10 }}>
            <button
                onClick={() => onPageChange?.(safePage - 1)}
                disabled={safePage <= 1}
                style={{
                    fontSize: mobile ? 10 : 11,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: `1px solid ${T.cardBorder}`,
                    background: T.surface,
                    color: safePage <= 1 ? T.t3 : T.t2,
                    cursor: safePage <= 1 ? "not-allowed" : "pointer",
                }}
            >
                Prev
            </button>
            <span style={{ fontSize: mobile ? 10 : 11, color: T.t2 }}>Page {safePage} / {safeTotal}</span>
            <button
                onClick={() => onPageChange?.(safePage + 1)}
                disabled={safePage >= safeTotal}
                style={{
                    fontSize: mobile ? 10 : 11,
                    padding: "4px 10px",
                    borderRadius: 6,
                    border: `1px solid ${T.cardBorder}`,
                    background: T.surface,
                    color: safePage >= safeTotal ? T.t3 : T.t2,
                    cursor: safePage >= safeTotal ? "not-allowed" : "pointer",
                }}
            >
                Next
            </button>
        </div>
    );
}

/* -- Table Shimmer (grid-only loading for pagination) -- */
function TableShimmer({ cols = 5, rows = 5, mobile }: any) {
    return (
        <div style={{ padding: mobile ? 8 : 0 }}>
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className="rpt-flex-row rpt-gap-12" style={{ padding: "10px 10px", borderBottom: `1px solid ${T.divider}` }}>
                    {Array.from({ length: cols }).map((_, j) => (
                        <Bone key={j} w={j === 0 ? 60 : j === 1 ? 120 : 80} h={14} r={4} />
                    ))}
                </div>
            ))}
        </div>
    );
}

/* -- Event Date Picker (for filtering within event date range) -- */
function EventDatePicker({ value, onChange, minDate, maxDate, mobile }: any) {
    const inputRef = useRef<any>(null);
    const displayValue = value ? fmtEventDate(value) : "All dates";

    const handlePrev = () => {
        if (!value) return;
        const d = new Date(value + "T12:00:00");
        d.setDate(d.getDate() - 1);
        const prev = d.toISOString().split("T")[0];
        if (!minDate || prev >= minDate) onChange(prev);
    };

    const handleNext = () => {
        if (!value) return;
        const d = new Date(value + "T12:00:00");
        d.setDate(d.getDate() + 1);
        const next = d.toISOString().split("T")[0];
        if (!maxDate || next <= maxDate) onChange(next);
    };

    const canPrev = !!value && (!minDate || value > minDate);
    const canNext = !!value && (!maxDate || value < maxDate);

    return (
        <div className="rpt-flex-row rpt-flex-center rpt-gap-6" style={{ position: "relative" }}>
            <label style={{ fontSize: mobile ? 10 : 11, fontWeight: 600, color: T.t3, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: 0.5 }}>
                Event Date
            </label>
            <button
                onClick={handlePrev}
                disabled={!canPrev}
                style={{
                    width: mobile ? 24 : 26, height: mobile ? 24 : 26, borderRadius: 6,
                    border: `1px solid ${T.cardBorder}`, background: T.surface,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: canPrev ? "pointer" : "not-allowed", color: canPrev ? T.t2 : T.t3,
                }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>chevron_left</span>
            </button>
            <button
                type="button"
                onClick={() => inputRef.current?.showPicker?.()}
                style={{
                    padding: mobile ? "4px 10px" : "5px 12px",
                    fontSize: mobile ? 11 : 12, fontWeight: 500, color: T.t1,
                    background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 8,
                    cursor: "pointer", fontFamily: T.font, display: "flex", alignItems: "center", gap: 6,
                    position: "relative", minWidth: mobile ? 120 : 140, justifyContent: "center",
                }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 14, color: T.t3 }}>calendar_today</span>
                {displayValue}
                <input
                    ref={inputRef}
                    type="date"
                    value={value || ""}
                    min={minDate}
                    max={maxDate}
                    onChange={(e) => onChange(e.target.value)}
                    style={{
                        position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                        opacity: 0, cursor: "pointer", border: "none",
                    }}
                />
            </button>
            <button
                onClick={handleNext}
                disabled={!canNext}
                style={{
                    width: mobile ? 24 : 26, height: mobile ? 24 : 26, borderRadius: 6,
                    border: `1px solid ${T.cardBorder}`, background: T.surface,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: canNext ? "pointer" : "not-allowed", color: canNext ? T.t2 : T.t3,
                }}
            >
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>chevron_right</span>
            </button>
        </div>
    );
}

/* =============================================================================
   KPI CARDS
   ============================================================================= */

/* -- Order Overview: tile design (white card + icon-colored glow, EOD-style) -- */
const ORDER_OVERVIEW_CARDS = [
    { key: "totalPreOrderSales", label: "Total Sales", icon: "trending_up", iconColor: T.green, blurColor: T.greenSoft, getValue: (ov, kpis) => fmtCurrency(ov.totalPreOrderSales ?? kpis?.totalRevenue ?? 0, 2) },
    { key: "totalPreOrders", label: "Total Orders", icon: "receipt_long", iconColor: T.blue, blurColor: T.blueSoft, getValue: (ov, kpis) => fmtNum(ov.totalPreOrders ?? kpis?.totalOrders ?? 0) },
    // { key: "avgOrderValue", label: "Avg Order Value", icon: "shopping_cart", iconColor: T.blueDark, blurColor: T.blueDarkSoft, getValue: (ov, kpis) => "$" + num(kpis?.avgOrderValue ?? 0).toFixed(2) },
    { key: "totalPreOrderQty", label: "Total Ordered Items", icon: "inventory_2", iconColor: T.teal, blurColor: T.tealSoft, getValue: (ov) => fmtNum(ov.totalPreOrderQty ?? 0) },
    { key: "cancelledPreOrders", label: "Cancelled Orders", icon: "cancel", iconColor: T.red, blurColor: T.redSoft, getValue: (ov, kpis) => fmtNum(ov.cancelledPreOrders ?? kpis?.cancellations ?? 0) },
    { key: "cancelledPreOrderSales", label: "Refund", icon: "undo", iconColor: T.red, blurColor: T.redSoft, getValue: (ov) => fmtCurrency(ov.cancelledPreOrderSales ?? 0, 2) },
];

function EventOrderOverview({ orderOverview = {}, kpis = {}, mobile }: any) {
    const ov = orderOverview;
    return (
        <div style={{ marginBottom: mobile ? 12 : 16 }}>
            <div className={`rpt-flex-row rpt-flex-wrap ${mobile ? "" : "rpt-gap-16"}`} style={{ gap: mobile ? 10 : 16 }}>
                {ORDER_OVERVIEW_CARDS.map(({ key, label, icon, iconColor, blurColor, getValue }) => (
                    <div
                        key={key}
                        className={`rpt-tile ${mobile ? "rpt-tile--compact" : ""}`}
                        style={{
                            flex: "1 1 155px",
                            minWidth: mobile ? 100 : 180,
                            background: T.card,
                            border: `1px solid ${T.cardBorder}`,
                            borderRadius: 16,
                            padding: mobile ? "12px 14px" : "20px 22px",
                            boxShadow: T.cardShadow,
                            position: "relative",
                            overflow: "hidden",
                        }}
                    >
                        {blurColor && (
                            <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: blurColor, filter: "blur(20px)" }} />
                        )}
                        <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginBottom: mobile ? 6 : 8, position: "relative" }}>
                            <span className="rpt-tile__label">{label}</span>
                            <span className="material-symbols-rounded" style={{ fontSize: mobile ? 16 : 18, color: iconColor || T.t3 }}>{icon}</span>
                        </div>
                        <div className={mobile ? "rpt-tile__value--sm" : "rpt-tile__value"} style={{ color: T.t1, position: "relative", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {getValue(ov, kpis)}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* -- Order Details table -- */
function OrderDetailsTable({ data, mobile, page = 1, onPageChange, loading, datePicker }: any) {
    const { content = [], totalElements = 0, totalPages = 1 } = data || {};
    const rows = Array.isArray(content) ? content : [];
    return (
        <Card mobile={mobile}>
            <SecTitle sub={`${totalElements} order(s)`} mobile={mobile} right={datePicker}>Order Details</SecTitle>
            {loading ? (
                <TableShimmer cols={9} rows={10} mobile={mobile} />
            ) : rows.length === 0 ? (
                <SectionEmpty kpiName="order details" size="medium" minHeight={220} />
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.font }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${T.divider}` }}>
                                {["Order", "Items", "Order date", "Customer", "Contact", "Pickup date", "Pickup slot", "Qty", "Total"].map((h) => (
                                    <th key={h} style={{ textAlign: h === "Qty" || h === "Total" ? "right" : "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                    <td style={{ padding: "10px 10px", fontWeight: 600, color: T.accent, whiteSpace: "nowrap" }}>#{row.orderNo ?? row.id ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.items ?? row.itemNames ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{row.orderDate ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t1, fontWeight: 500 }}>{row.customerFullName ?? row.customerName ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{row.contactNumber ?? row.phone ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{row.pickUpDate ?? row.pickupDate ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{row.timeSlot ?? row.pickupSlot ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t1, textAlign: "right" }}>{row.qty ?? row.quantity ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", fontWeight: 600, color: T.t1, whiteSpace: "nowrap", textAlign: "right" }}>{row.orderTotal != null ? (typeof row.orderTotal === "number" ? fmtCurrency(row.orderTotal, 2) : row.orderTotal) : "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <TablePager page={page} totalPages={totalPages || 1} onPageChange={onPageChange} mobile={mobile} />
        </Card>
    );
}

/* -- Item Sales table (portal-aligned) -- */
function ItemSalesTable({ data, mobile, page = 1, onPageChange, loading }: any) {
    const { content = [], totalElements = 0, totalPages = 1 } = data || {};
    const items = Array.isArray(content) ? content : [];
    return (
        <Card mobile={mobile}>
            <SecTitle sub={`Item-level quantities and revenue (${totalElements})`} mobile={mobile}>Item Sales</SecTitle>
            {loading ? (
                <TableShimmer cols={3} rows={5} mobile={mobile} />
            ) : items.length === 0 ? (
                <SectionEmpty kpiName="item sales" size="medium" minHeight={200} />
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.font }}>
                        <thead>
                            <tr style={{ borderBottom: `2px solid ${T.divider}` }}>
                                <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Item</th>
                                <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Sold Qty</th>
                                <th style={{ textAlign: "right", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5 }}>Item Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                    <td style={{ padding: "10px 10px", color: T.t1, fontWeight: 500 }}>{item.name ?? "—"}</td>
                                    <td style={{ padding: "10px 10px", color: T.t2, textAlign: "right" }}>{fmtNum(item.qty ?? 0)}</td>
                                    <td style={{ padding: "10px 10px", fontWeight: 600, color: T.t1, textAlign: "right" }}>{fmtCurrency(item.revenue ?? 0)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            <TablePager page={page} totalPages={totalPages || 1} onPageChange={onPageChange} mobile={mobile} />
        </Card>
    );
}

const KPI_DEFS = [
    { key: "revenue", label: "EVENT REVENUE", icon: "payments", fmt: (k) => fmtCurrency(k.totalRevenue), delta: (k) => k.revenueDelta },
    { key: "orders", label: "TOTAL ORDERS", icon: "receipt_long", fmt: (k) => fmtNum(k.totalOrders), delta: (k) => k.ordersDelta },
    // { key: "aov", label: "AVG ORDER VALUE", icon: "shopping_cart", fmt: (k) => "$" + num(k.avgOrderValue).toFixed(2), delta: (k) => k.aovDelta },
    { key: "fulfillment", label: "FULFILLMENT RATE", icon: "check_circle", fmt: (k) => k.fulfillmentRate + "%", delta: (k) => k.fulfillmentDelta },
    { key: "noShows", label: "NO-SHOWS", icon: "person_off", fmt: (k) => fmtNum(k.noShows), delta: (k) => k.noShowsDelta, drillable: true },
    { key: "cancellations", label: "CANCELLATIONS", icon: "cancel", fmt: (k) => fmtNum(k.cancellations), delta: (k) => k.cancellationsDelta, drillable: true },
];

function KpiCardDesktop({ label, value, delta, icon }: any) {
    const deltaStr = delta != null ? String(delta) : null;
    const isPositive = deltaStr ? deltaStr.startsWith("+") : false;
    const isNegative = deltaStr ? deltaStr.startsWith("-") : false;
    const dColor = isPositive ? T.green : isNegative ? T.red : T.t2;
    const blurColor = isPositive ? T.greenSoft : isNegative ? T.redSoft : T.accentSoft;
    return (
        <div style={{
            flex: 1, minWidth: 155, background: T.card,
            border: `1px solid ${T.cardBorder}`, borderRadius: 14,
            padding: "20px 22px", boxShadow: T.cardShadow,
            position: "relative", overflow: "hidden",
        }}>
            <div style={{ position: "absolute", top: -20, right: -20, width: 80, height: 80, borderRadius: "50%", background: blurColor, filter: "blur(20px)" }} />
            <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ position: "relative" }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: T.t2, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
                <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.t3 }}>{icon}</span>
            </div>
            <div style={{ fontSize: 28, fontWeight: 700, color: T.t1, letterSpacing: -0.5, marginTop: 6, position: "relative", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
            {deltaStr && (
                /* GLOBAL-DP-4 (ZAR-1663): trailing comparison text removed \u2014 the basis is
                   stated once in the unified date control. */
                <div title="Compared with the prior year" style={{ fontSize: 12, fontWeight: 600, color: dColor, marginTop: 4, position: "relative" }}>
                    {isPositive ? "\u2191" : isNegative ? "\u2193" : ""} {deltaStr}
                </div>
            )}
        </div>
    );
}

function KpiCardMobile({ label, value, delta }: any) {
    const deltaStr = delta != null ? String(delta) : null;
    const isPositive = deltaStr ? deltaStr.startsWith("+") : false;
    const isNegative = deltaStr ? deltaStr.startsWith("-") : false;
    const dColor = isPositive ? T.green : isNegative ? T.red : T.t2;
    return (
        <div style={{
            background: T.card, border: `1px solid ${T.cardBorder}`,
            borderRadius: 12, padding: "12px 10px",
            boxShadow: T.shadow, textAlign: "center",
        }}>
            <div className="rpt-card-label-sm" style={{ color: T.t3 }}>{label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: T.t1, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
            {deltaStr && (
                <div style={{ fontSize: 9, fontWeight: 600, color: dColor, marginTop: 3 }}>
                    {isPositive ? "\u2191" : isNegative ? "\u2193" : ""} {deltaStr}
                </div>
            )}
        </div>
    );
}

/* =============================================================================
   CHART COMPONENTS
   ============================================================================= */

/* -- Prep Quantity List (horizontal bar chart) -- */
function PrepQuantityList({ data, mobile, page = 1, onPageChange, loading }: any) {
    const { content = [], totalElements = 0, totalPages = 1 } = data || {};
    const [mode, setMode] = useState("qty"); // "qty" | "revenue"
    const sorted = useMemo(() => {
        const items = Array.isArray(content) ? content : [];
        return [...items].sort((a, b) => (mode === "qty" ? b.qty - a.qty : b.revenue - a.revenue));
    }, [content, mode]);
    const maxVal = sorted.length > 0 ? Math.max(...sorted.map(i => mode === "qty" ? i.qty : i.revenue)) : 1;

    return (
        <Card mobile={mobile} style={{ flex: mobile ? "1 1 auto" : 2 }}>
            <SecTitle sub={`Item-level pre-order volumes (${totalElements})`} mobile={mobile}
                right={
                    <div className="rpt-flex-row rpt-gap-4">
                        {[{ k: "qty", l: "By Quantity" }, { k: "revenue", l: "By Revenue" }].map(t => (
                            <button key={t.k} onClick={() => setMode(t.k)} style={{
                                fontSize: 10, fontWeight: mode === t.k ? 600 : 400, padding: "3px 10px",
                                borderRadius: 6, border: `1px solid ${mode === t.k ? T.accent : T.cardBorder}`,
                                background: mode === t.k ? T.accentSoft : "transparent", color: mode === t.k ? T.accent : T.t3,
                                cursor: "pointer",
                            }}>{t.l}</button>
                        ))}
                    </div>
                }
            >Prep Quantity List</SecTitle>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                {loading ? (
                    <TableShimmer cols={3} rows={5} mobile={mobile} />
                ) : sorted.length === 0 ? (
                    <SectionEmpty kpiName="prep quantity" minHeight={mobile ? 100 : 140} />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 6 : 8 }}>
                        {sorted.map((item, i) => {
                            const val = mode === "qty" ? item.qty : item.revenue;
                            const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
                            return (
                                <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-10">
                                    <div style={{ width: mobile ? 110 : 170, fontSize: mobile ? 9 : 11, color: T.t2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>{item.name}</div>
                                    <div style={{ flex: 1, height: mobile ? 10 : 14, background: T.hover, borderRadius: 4, overflow: "hidden" }}>
                                        <div style={{ height: "100%", width: `${pct}%`, background: T.accent, borderRadius: 4, transition: "width 0.3s ease" }} />
                                    </div>
                                    <div style={{ fontSize: mobile ? 9 : 11, fontWeight: 600, color: T.t1, minWidth: mobile ? 36 : 52, textAlign: "right" }}>
                                        {mode === "qty" ? fmtNum(val) : fmtCurrency(val)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            <TablePager page={page} totalPages={totalPages || 1} onPageChange={onPageChange} mobile={mobile} />
        </Card>
    );
}

/* -- Booking Curve (Area Chart) -- */
function BookingCurve({ data, mobile }: any) {
    return (
        <Card mobile={mobile} style={{ flex: mobile ? "1 1 auto" : 2 }}>
            <SecTitle sub="Cumulative orders: current vs prior year" mobile={mobile}>Booking Curve</SecTitle>
            {data.length === 0 ? (
                <SectionEmpty kpiName="booking curve" minHeight={mobile ? 140 : 240} />
            ) : (
                <div style={{ height: mobile ? 120 : 220 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data}>
                            <defs>
                                <linearGradient id="bcCurrent" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={T.accent} stopOpacity={0.2} />
                                    <stop offset="95%" stopColor={T.accent} stopOpacity={0.01} />
                                </linearGradient>
                                <linearGradient id="bcPrior" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={T.t3} stopOpacity={0.1} />
                                    <stop offset="95%" stopColor={T.t3} stopOpacity={0.01} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                            <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 7 : 9, fill: T.t3, fontFamily: T.font }} />
                            <YAxis hide />
                            <Tooltip content={<MTooltip />} />
                            <Area type="monotone" dataKey="current" name="Current" stroke={T.accent} strokeWidth={2.5} fill="url(#bcCurrent)" dot={false} />
                            <Area type="monotone" dataKey="prior" name="Prior Year" stroke={T.t3} strokeWidth={1.5} strokeDasharray="5 3" fill="url(#bcPrior)" dot={false} />
                        </AreaChart>
                    </ResponsiveContainer>
                    <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 6 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.t2 }}>
                            <span style={{ width: 12, height: 3, borderRadius: 2, background: T.accent }} /> Current
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: T.t2 }}>
                            <span style={{ width: 12, height: 3, borderRadius: 2, background: T.t3 }} /> Prior Year
                        </div>
                    </div>
                </div>
            )}
        </Card>
    );
}

/* -- Pickup by slot (Bar Chart) -- */
function PickupSlotVolume({ data, mobile, datePicker, loading }: any) {
    const peakI = data.length > 0 ? data.reduce((m, x, i, a) => x.orders > a[m].orders ? i : m, 0) : 0;
    const peakValue = data.length > 0 ? Number(data[peakI]?.orders) || 0 : 0;
    const activeSlots = data.filter((d: any) => Number(d?.orders) > 0);
    const avgOrders = activeSlots.length > 0 ? activeSlots.reduce((s: number, d: any) => s + Number(d.orders), 0) / activeSlots.length : 0;
    const totalOrders = data.reduce((s: number, d: any) => s + (Number(d?.orders) || 0), 0);
    const showAvgLine = avgOrders > 0 && activeSlots.length >= 2 && avgOrders < peakValue;
    const avgDisplay = avgOrders >= 10 ? Math.round(avgOrders).toLocaleString() : avgOrders.toFixed(1);
    const TooltipPickup = ({ active, payload, label }: any) => {
        if (!active || !payload?.length) return null;
        const d = payload[0]?.payload;
        return (
            <div style={{ background: T.surface, border: `1px solid ${T.cardBorder}`, borderRadius: 8, padding: "8px 12px", boxShadow: T.cardShadow, fontFamily: T.font }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: T.t1, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 10, color: T.t2 }}>Orders: <span style={{ fontWeight: 600, color: T.t1 }}>{d?.orders ?? 0}</span></div>
                {(d?.sales != null && d.sales > 0) && <div style={{ fontSize: 10, color: T.t2 }}>Sales: <span style={{ fontWeight: 600, color: T.t1 }}>{fmtCurrency(d.sales, 2)}</span></div>}
                {(d?.itemQty != null && d.itemQty > 0) && <div style={{ fontSize: 10, color: T.t2 }}>Item Qty: <span style={{ fontWeight: 600, color: T.t1 }}>{d.itemQty}</span></div>}
            </div>
        );
    };
    return (
        <Card mobile={mobile}>
            <SecTitle sub="Orders per pickup time slot" mobile={mobile}>Pickup by slot</SecTitle>
            {datePicker && <div style={{ marginBottom: mobile ? 8 : 12 }}>{datePicker}</div>}
            {loading ? (
                <TableShimmer cols={3} rows={4} mobile={mobile} />
            ) : data.length === 0 ? (
                <SectionEmpty kpiName="pickup slots" minHeight={mobile ? 140 : 200} />
            ) : (
                <>
                <div style={{ height: mobile ? 160 : 200 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={data} margin={{ top: 18, right: mobile ? 52 : 56, left: mobile ? 30 : 20, bottom: mobile ? 36 : 24 }}>
                            <CartesianGrid vertical={false} stroke={T.divider} strokeOpacity={0.5} />
                            <XAxis dataKey="slot" axisLine={false} tickLine={false} tick={{ fontSize: mobile ? 8 : 10, fill: T.t3, fontFamily: T.font }} interval={0} angle={mobile ? -45 : -25} textAnchor="end" height={mobile ? 50 : 60} />
                            <YAxis hide />
                            <Tooltip content={<TooltipPickup />} />
                            {showAvgLine && (
                                <ReferenceLine y={avgOrders} stroke={T.t3} strokeDasharray="4 3" ifOverflow="extendDomain" label={{ value: `Avg: ${avgDisplay}`, position: "right", fill: T.t2, fontSize: mobile ? 9 : 10, fontWeight: 500, offset: 6 }} />
                            )}
                            <Bar dataKey="orders" name="Orders" radius={[3, 3, 0, 0]} maxBarSize={mobile ? 16 : 28}>
                                {data.map((_, i) => <Cell key={i} fill={i === peakI ? T.accent : "rgba(210,58,59,0.4)"} />)}
                                <LabelList dataKey="orders" content={(props: any) => {
                                    const { x, y, width, height, index, value } = props;
                                    if (index !== peakI || !Number(value)) return null;
                                    const bw = Number(width);
                                    const bh = Number(height);
                                    const cx = Number(x) + bw / 2;
                                    const showInside = bw >= 18 && bh >= 20;
                                    return (
                                        <g>
                                            <text x={cx} y={Number(y) - 4} textAnchor="middle" fill={T.accent} fontSize={mobile ? 9 : 10} fontWeight={700}>{value}</text>
                                        </g>
                                    );
                                }} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
                {peakValue > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: mobile ? 6 : 10, paddingTop: mobile ? 6 : 10, borderTop: `1px solid ${T.divider}`, fontSize: mobile ? 10 : 11, color: T.t3, gap: 6 }}>
                        <span>Peak: <span style={{ color: T.accent, fontWeight: 600 }}>{data[peakI]?.slot ?? "--"}</span>{peakValue > 0 ? ` — ${peakValue}` : ""}</span>
                        <span>Avg: <span style={{ color: T.t1, fontWeight: 600 }}>{avgOrders > 0 ? avgDisplay : "--"}</span></span>
                        <span>Total: <span style={{ color: T.t1, fontWeight: 600 }}>{totalOrders.toLocaleString()}</span></span>
                    </div>
                )}
                </>
            )}
        </Card>
    );
}

/* -- Fulfillment Status (stacked progress bars) -- */
function FulfillmentStatus({ data, mobile }: any) {
    const total = data.reduce((s, d) => s + d.count, 0);
    return (
        <Card mobile={mobile} style={{ flex: 1 }}>
            <SecTitle sub="Order fulfillment breakdown" mobile={mobile}>Fulfillment Status</SecTitle>
            {data.length === 0 ? (
                <SectionEmpty kpiName="fulfillment status" minHeight={mobile ? 100 : 140} />
            ) : (
                <>
                    {/* Stacked bar */}
                    <div style={{ display: "flex", height: mobile ? 12 : 16, borderRadius: 8, overflow: "hidden", marginBottom: mobile ? 12 : 16 }}>
                        {data.map((d, i) => (
                            <div key={i} style={{ flex: d.count, background: d.color, opacity: 0.85, transition: "flex 0.3s ease" }} title={`${d.status}: ${d.count}`} />
                        ))}
                    </div>
                    {/* Legend rows */}
                    <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 8 : 10 }}>
                        {data.map((d, i) => (
                            <div key={i} className="rpt-flex-row rpt-flex-center rpt-flex-between">
                                <div className="rpt-flex-row rpt-flex-center rpt-gap-8">
                                    <span style={{ width: 8, height: 8, borderRadius: "50%", background: d.color, flexShrink: 0 }} />
                                    <StatusPill status={d.status} />
                                </div>
                                <div className="rpt-flex-row rpt-flex-center rpt-gap-12">
                                    <span style={{ fontSize: mobile ? 12 : 14, fontWeight: 700, color: T.t1 }}>{fmtNum(d.count)}</span>
                                    <span style={{ fontSize: mobile ? 9 : 10, color: T.t3, minWidth: 36, textAlign: "right" }}>{d.pct}%</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: mobile ? 10 : 14, padding: "8px 12px", background: T.accentSoft, borderRadius: 8, textAlign: "center" }}>
                        <span style={{ fontSize: mobile ? 10 : 11, color: T.t2 }}>Total: </span>
                        <span style={{ fontSize: mobile ? 12 : 14, fontWeight: 700, color: T.accent }}>{fmtNum(total)}</span>
                    </div>
                </>
            )}
        </Card>
    );
}

/* -- By Channel (Donut Chart) -- */
function ChannelMix({ data, mobile }: any) {
    const total = data.reduce((s, d) => s + d.value, 0);
    return (
        <Card mobile={mobile} style={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
            <SecTitle sub="Order source distribution" mobile={mobile}>By Channel</SecTitle>
            {data.length === 0 ? (
                <SectionEmpty kpiName="channel mix" minHeight={mobile ? 140 : 160} />
            ) : (
                <div style={{ display: "flex", flexDirection: mobile ? "column" : "row", alignItems: "center", gap: mobile ? 12 : 24, minWidth: 0, overflow: "hidden" }}>
                    <div style={{ width: mobile ? 110 : 140, height: mobile ? 110 : 140, flexShrink: 0, position: "relative" }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={data} cx="50%" cy="50%" innerRadius={mobile ? 34 : 42} outerRadius={mobile ? 50 : 62} dataKey="value" strokeWidth={0}>
                                    {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", textAlign: "center" }}>
                            <div style={{ fontSize: mobile ? 14 : 16, fontWeight: 700, color: T.t1 }}>{total}%</div>
                        </div>
                    </div>
                    <div className="rpt-flex-col rpt-gap-8" style={{ minWidth: 0, flexShrink: 0 }}>
                        {data.map((e, i) => (
                            <div key={i} className="rpt-flex-row rpt-flex-center rpt-gap-8" style={{ minWidth: 0 }}>
                                <span style={{ width: 8, height: 8, borderRadius: 2, background: e.color, flexShrink: 0 }} />
                                <span style={{ fontSize: 11, color: T.t2, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.name}</span>
                                <span style={{ fontSize: 11, fontWeight: 600, color: T.t1, flexShrink: 0 }}>{e.value}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Card>
    );
}

/* -- Customer Run Sheet (Desktop Table) -- */
const STATUS_FILTERS = ["All", "Picked Up", "Ready", "Preparing", "Ordered", "No-Show", "Cancelled"];

function CustomerRunSheetTable({ data, mobile, dateRangeLabel }: any) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    const filtered = useMemo(() => {
        let list = data || [];
        if (statusFilter !== "All") list = list.filter(c => c.fulfillment === statusFilter);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(c => (c.name || "").toLowerCase().includes(q) || (c.id || "").toLowerCase().includes(q) || (c.phone || "").includes(q) || (c.email || "").toLowerCase().includes(q));
        }
        return list;
    }, [data, statusFilter, search]);

    const runSheetExportRows = useCallback(() => ({
        headers: ["Order", "Customer", "Phone", "Email", "Pickup Slot", "Items", "Total", "Payment", "Status"],
        rows: filtered.map(c => [c.id, c.name, c.phone, c.email, c.slot, c.items, c.total, c.payment, c.fulfillment].map(v => String(v ?? ""))),
    }), [filtered]);
    const handlePrint = useCallback(() => {
        // Capacitor native can't window.print() — export the sheet as a shared PDF instead.
        if (isNativePlatform()) {
            const { headers, rows } = runSheetExportRows();
            const subtitle = `Exported on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`;
            void exportTablePdfNative("Customer Run Sheet", subtitle, headers, rows, "customer-run-sheet.pdf");
            return;
        }
        window.print();
    }, [runSheetExportRows]);
    const handleCSV = useCallback(() => {
        const { headers, rows } = runSheetExportRows();
        // Native saves .xlsx so the currency symbol renders in Google Sheets Android too.
        if (isNativePlatform()) {
            void exportTableXlsxNative(headers, rows, "customer-run-sheet.xlsx");
            return;
        }
        const csv = `${CSV_BOM}${[headers, ...rows].map(r => r.map(escapeCsvCell).join(",")).join("\r\n")}`;
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = "customer-run-sheet.csv"; a.click();
        URL.revokeObjectURL(url);
    }, [runSheetExportRows]);

    return (
        <Card mobile={mobile}>
            <SecTitle sub="Live pickup tracking" mobile={mobile}>Customer Run Sheet</SecTitle>
            {/* Toolbar */}
            <div className="rpt-flex-row rpt-flex-wrap rpt-flex-center rpt-gap-8" style={{ marginBottom: 14 }}>
                <div style={{ position: "relative", flex: "1 1 200px", maxWidth: 300 }}>
                    <span className="material-symbols-rounded" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: T.t3 }}>search</span>
                    <input
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search orders..."
                        style={{ width: "100%", padding: "7px 10px 7px 30px", border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, fontFamily: T.font, outline: "none", background: T.surface, color: T.t1 }}
                    />
                </div>
                <div className="rpt-flex-row rpt-flex-wrap rpt-gap-4">
                    {STATUS_FILTERS.map(s => (
                        <button key={s} onClick={() => setStatusFilter(s)} style={{
                            fontSize: 10, fontWeight: statusFilter === s ? 600 : 400, padding: "4px 10px",
                            borderRadius: 6, border: `1px solid ${statusFilter === s ? T.accent : T.cardBorder}`,
                            background: statusFilter === s ? T.accentSoft : "transparent", color: statusFilter === s ? T.accent : T.t3,
                            cursor: "pointer",
                        }}>{s}</button>
                    ))}
                </div>
                {filtered.length > 0 && (
                <div className="rpt-flex-row rpt-gap-6" style={{ marginLeft: "auto" }}>
                    <button onClick={handlePrint} className="rpt-flex-row rpt-flex-center rpt-gap-4" style={{ fontSize: 11, fontWeight: 500, padding: "5px 12px", borderRadius: 8, border: `1px solid ${T.cardBorder}`, background: T.surface, color: T.t2, cursor: "pointer" }}>
                        <span className="material-symbols-rounded" style={{ fontSize: 14 }}>print</span> Print
                    </button>
                    <ReportSectionExportButton
                        dateRangeLabel={dateRangeLabel}
                        title="Customer Run Sheet"
                        filename="customer-run-sheet"
                        outletScoped={false}
                        buildExport={() => ({
                            headers: ["Order", "Customer", "Phone", "Email", "Pickup Slot", "Items", "Total", "Payment", "Status"],
                            rows: filtered.map(c => [c.id, c.name, c.phone, c.email, c.slot, c.items, c.total, c.payment, c.fulfillment]),
                        })}
                    />
                </div>
                )}
            </div>
            {/* Table */}
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: T.font }}>
                    <thead>
                        <tr style={{ borderBottom: `2px solid ${T.divider}` }}>
                            {["Order", "Customer", "Phone", "Pickup Slot", "Items", "Total", "Payment", "Status"].map(h => (
                                <th key={h} style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, fontWeight: 600, color: T.t3, textTransform: "uppercase", letterSpacing: 0.5, whiteSpace: "nowrap" }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan={8} style={{ padding: 0 }}><SectionEmpty kpiName="order details" size="medium" minHeight={200} /></td></tr>
                        ) : filtered.map((c, i) => (
                            <tr key={i} style={{ borderBottom: `1px solid ${T.divider}` }}>
                                <td style={{ padding: "10px 10px", fontWeight: 600, color: T.accent, whiteSpace: "nowrap" }}>{c.id}</td>
                                <td style={{ padding: "10px 10px", color: T.t1, fontWeight: 500 }}>{c.name}</td>
                                <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{c.phone}</td>
                                <td style={{ padding: "10px 10px", color: T.t2, whiteSpace: "nowrap" }}>{c.slot}</td>
                                <td style={{ padding: "10px 10px", color: T.t2, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.items}</td>
                                <td style={{ padding: "10px 10px", fontWeight: 600, color: T.t1, whiteSpace: "nowrap" }}>{c.total}</td>
                                <td style={{ padding: "10px 10px" }}><StatusPill status={c.payment} /></td>
                                <td style={{ padding: "10px 10px" }}><StatusPill status={c.fulfillment} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

/* =============================================================================
   MOBILE DRILL-IN SCREENS
   ============================================================================= */

function DrillInHeader({ title, onBack }: any) {
    return (
        <div className="rpt-flex-row rpt-flex-center rpt-gap-10" style={{
            padding: "12px 16px",
            background: T.surface, borderBottom: `1px solid ${T.divider}`,
        }}>
            <button onClick={onBack} style={{
                width: 28, height: 28, borderRadius: 8, background: T.surface,
                border: `1px solid ${T.divider}`, display: "flex", alignItems: "center", justifyContent: "center",
                cursor: "pointer", flexShrink: 0,
            }}>
                <span className="material-symbols-rounded" style={{ fontSize: 16, color: T.t1 }}>arrow_back</span>
            </button>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.t1 }}>{title}</div>
        </div>
    );
}

function PrepListDrillIn({ data, onBack, scrollRef }: any) {
    return (
        <>
            <DrillInHeader title="Prep Quantity List" onBack={onBack} />
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                <PrepQuantityList data={data.prepQuantity} mobile />
            </div>
        </>
    );
}

function PickupSlotsDrillIn({ data, onBack, scrollRef }: any) {
    return (
        <>
            <DrillInHeader title="Pickup Slot Volume" onBack={onBack} />
            <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                <PickupSlotVolume data={data.pickupSlots} mobile />
            </div>
        </>
    );
}

function BookingCurveDrillIn({ data, onBack }: any) {
    return (
        <>
            <DrillInHeader title="Booking Curve" onBack={onBack} />
            <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                <BookingCurve data={data.bookingCurve} mobile />
            </div>
        </>
    );
}

function CustomerSheetDrillIn({ data, onBack, scrollRef }: any) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    const filtered = useMemo(() => {
        let list = data.customerRunSheet || [];
        if (statusFilter !== "All") list = list.filter(c => c.fulfillment === statusFilter);
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter(c => (c.name || "").toLowerCase().includes(q) || (c.id || "").toLowerCase().includes(q) || (c.phone || "").includes(q));
        }
        return list;
    }, [data.customerRunSheet, statusFilter, search]);

    return (
        <>
            <DrillInHeader title="Customer Run Sheet" onBack={onBack} />
            <div ref={scrollRef} className="rpt-flex-col rpt-gap-10" style={{ flex: 1, overflowY: "auto", padding: 16 }}>
                {/* Search */}
                <div style={{ position: "relative" }}>
                    <span className="material-symbols-rounded" style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: T.t3 }}>search</span>
                    <input
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search orders..."
                        style={{ width: "100%", padding: "7px 10px 7px 30px", border: `1px solid ${T.cardBorder}`, borderRadius: 8, fontSize: 12, fontFamily: T.font, outline: "none", background: T.surface, color: T.t1, boxSizing: "border-box" }}
                    />
                </div>
                {/* Status Filter Pills */}
                <div className="rpt-flex-row rpt-flex-wrap rpt-gap-4">
                    {STATUS_FILTERS.map(s => (
                        <button key={s} onClick={() => setStatusFilter(s)} style={{
                            fontSize: 9, fontWeight: statusFilter === s ? 600 : 400, padding: "3px 8px",
                            borderRadius: 6, border: `1px solid ${statusFilter === s ? T.accent : T.cardBorder}`,
                            background: statusFilter === s ? T.accentSoft : "transparent", color: statusFilter === s ? T.accent : T.t3,
                            cursor: "pointer",
                        }}>{s}</button>
                    ))}
                </div>
                {/* Customer Cards */}
                {filtered.length === 0 ? (
                    <SectionEmpty kpiName="order details" minHeight={160} />
                ) : filtered.map((c, i) => (
                    <Card key={i} mobile style={{ padding: 12 }}>
                        <div className="rpt-flex-row rpt-flex-between" style={{ alignItems: "flex-start", marginBottom: 6 }}>
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: T.accent }}>{c.id}</div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: T.t1, marginTop: 2 }}>{c.name}</div>
                            </div>
                            <StatusPill status={c.fulfillment} />
                        </div>
                        <div style={{ fontSize: 10, color: T.t2, marginBottom: 4 }}>{c.phone}</div>
                        <div className="rpt-flex-row rpt-flex-between rpt-flex-center" style={{ marginTop: 6 }}>
                            <div style={{ fontSize: 10, color: T.t3 }}>Pickup: <span style={{ fontWeight: 600, color: T.t1 }}>{c.slot}</span></div>
                            <div style={{ fontSize: 10, color: T.t3 }}>Total: <span style={{ fontWeight: 600, color: T.t1 }}>{c.total}</span></div>
                        </div>
                        <div style={{ fontSize: 10, color: T.t2, marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.items}</div>
                        <div style={{ marginTop: 6 }}><StatusPill status={c.payment} /></div>
                    </Card>
                ))}
            </div>
        </>
    );
}

/* =============================================================================
   DESKTOP CONTENT (inside DesktopLayout provider)
   ============================================================================= */
function EventDesktopContent() {
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? "", [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const [activeDate, setActiveDate] = useSharedReportDateFilter("Yesterday", { excludeToday: true });
    const [selectedEventSlug, setSelectedEventSlug] = useState("");
    const [orderDetailsPage, setOrderDetailsPage] = useState(1);
    const [itemSalesPage, setItemSalesPage] = useState(1);
    const [prepPage, setPrepPage] = useState(1);
    const [eventDate, setEventDate] = useState(today);
    const [pickupSlotDate, setPickupSlotDate] = useState(today);

    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activeDate, today, yesterday, storeTz);
        if (range) return range;
        return { startDate: today, endDate: today };
    }, [activeDate, today, yesterday, storeTz]);

    const { data, loading, paginationLoading, pickupSlotLoading, prepLoading, error, eventNames, eventNamesLoading, eventSlug, orderDetailsError, pickupSlotsError, retryOrderDetails, retryPickupSlots, retryOverview, retryPrep } = useEventData(locationId, token, dateRange, selectedEventSlug, { orderDetailsPage, itemSalesPage, prepPage }, eventDate || undefined, pickupSlotDate || undefined);

    // Sync selected event when eventNames load or change: keep selection valid or pick first
    useEffect(() => {
        if (!Array.isArray(eventNames) || eventNames.length === 0) return;
        const slug = eventNames[0]?.eventSlug ?? eventNames[0]?.slug ?? eventNames[0]?.id ?? "";
        const currentExists = eventNames.some((e) => (e.eventSlug ?? e.slug ?? e.id) === selectedEventSlug);
        if (!selectedEventSlug || !currentExists) setSelectedEventSlug(slug);
    }, [eventNames, selectedEventSlug]);

    // Reset date pickers and pages when event changes — default to today
    useEffect(() => {
        setOrderDetailsPage(1);
        setItemSalesPage(1);
        setPrepPage(1);
        setEventDate(today);
        setPickupSlotDate(today);
    }, [eventSlug, today]);

    useEffect(() => {
        setTopBarProps({
            breadcrumbs: "Reports",
            title: "Events & Pre-Order",
            dateFilters: undefined,
            onExport: () => {},
        });
    }, [setTopBarProps]);

    if (!locationId) return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: T.t3, padding: 24 }}>Select a location to view Events.</div>;

    const kpis = data.kpis;

    const orderDetailsDatePicker = (
        <EventDatePicker
            value={eventDate || today}
            onChange={(d) => { setEventDate(d); setOrderDetailsPage(1); }}
        />
    );

    const pickupSlotDatePicker = (
        <EventDatePicker
            value={pickupSlotDate || today}
            onChange={(d) => { setPickupSlotDate(d); }}
        />
    );

    return (
        <div className="rpt-page">
            {/* Event selector: page data is driven by selected event */}
            <EventSelector
                eventNames={eventNames}
                value={eventSlug}
                onChange={setSelectedEventSlug}
                loading={eventNamesLoading}
                disabled={!locationId}
                mobile={false}
            />

            {loading && <EventDesktopShimmer />}
            {error && <div style={{ padding: 24, textAlign: "center", color: T.red, fontSize: 13 }}>{error}</div>}
            {!loading && !error && (
            <>
            {/* 1. Order Overview (combined: pre-order summary + KPI-style cards in one container) */}
            <EventOrderOverview orderOverview={data.orderOverview} kpis={kpis} mobile={false} />

            {/* 2. Order Details table */}
            {orderDetailsError ? (
                <div className="rpt-card" style={{ padding: 0, borderRadius: 14, overflow: "hidden" }}>
                    <SectionError kpiName="order details" onRetry={retryOrderDetails} size="medium" minHeight={240} />
                </div>
            ) : (
                <OrderDetailsTable data={data.orderDetails} mobile={false} page={orderDetailsPage} onPageChange={setOrderDetailsPage} loading={paginationLoading} datePicker={orderDetailsDatePicker} />
            )}

            {/* 3. Pickup by slot (wide) + By Channel (narrow) — same row, matched height */}
            <div className="rpt-flex-row rpt-gap-16 rpt-row-equal-height" style={{ flexWrap: "wrap" }}>
                <div className="rpt-row-cell" style={{ flex: "2 1 480px", minWidth: 320, minHeight: 0 }}>
                    {pickupSlotsError ? (
                        <div className="rpt-card" style={{ padding: 0, borderRadius: 14, overflow: "hidden", height: "100%" }}>
                            <SectionError kpiName="pickup slots" onRetry={retryPickupSlots} minHeight={200} />
                        </div>
                    ) : (
                        <PickupSlotVolume data={data.pickupSlots} mobile={false} datePicker={pickupSlotDatePicker} loading={pickupSlotLoading} />
                    )}
                </div>
                <div className="rpt-row-cell" style={{ flex: "1 1 280px", minWidth: 260, minHeight: 0 }}>
                    <ChannelMix data={data.channelMix} mobile={false} />
                </div>
            </div>

            {/* 4. Item Sales (wide) + Prep Quantity List (narrow) — same row, matched height */}
            <div className="rpt-flex-row rpt-gap-16 rpt-row-equal-height" style={{ flexWrap: "wrap" }}>
                <div className="rpt-row-cell" style={{ flex: "2 1 480px", minWidth: 320, minHeight: 0 }}>
                    <ItemSalesTable data={data.itemSales} mobile={false} page={itemSalesPage} onPageChange={setItemSalesPage} loading={paginationLoading} />
                </div>
                <div className="rpt-row-cell" style={{ flex: "1 1 280px", minWidth: 260, minHeight: 0 }}>
                    <PrepQuantityList data={data.prepQuantity} mobile={false} page={prepPage} onPageChange={setPrepPage} loading={prepLoading} />
                </div>
            </div>
            {(data.customerRunSheet || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <CustomerRunSheetTable data={data.customerRunSheet} mobile={false} dateRangeLabel={exportPeriodFor(activeDate, dateRange)?.label} />
                </div>
            )}
            </>
            )}
        </div>
    );
}

function EventDesktopView() {
    return (
        <DesktopLayout>
            <EventDesktopContent />
        </DesktopLayout>
    );
}

/* =============================================================================
   MOBILE VIEW
   ============================================================================= */
function EventMobileView() {
    const navigate = useNavigate();
    const { auth } = useAuth();
    const { selectedBranch: branch } = useBranch();
    const setTopBarProps = useDesktopTopBar();
    const locationId = useMemo(() => branch?.id ?? branch?.locationId ?? findLocationId(auth) ?? "", [auth, branch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);

    const storeTz = useStoreTimeZone();
    const today = todayStr(storeTz);
    const yesterday = yesterdayStr(storeTz);
    const [activePill, setActivePill] = useSharedReportDateFilter("Yesterday", { excludeToday: true });
    const [drillIn, setDrillIn] = useState<any>(null); // null | "prep" | "pickup" | "booking" | "customer"
    useDrillHistoryBack(!!drillIn, () => setDrillIn(null));
    const [selectedEventSlug, setSelectedEventSlug] = useState("");
    const [orderDetailsPage, setOrderDetailsPage] = useState(1);
    const [itemSalesPage, setItemSalesPage] = useState(1);
    const [prepPage, setPrepPage] = useState(1);
    const [eventDate, setEventDate] = useState(today);
    const [pickupSlotDate, setPickupSlotDate] = useState(today);

    const dateRange = useMemo(() => {
        const range = getStartEndFromFilter(activePill, today, yesterday, storeTz);
        if (range) return range;
        return { startDate: today, endDate: today };
    }, [activePill, today, yesterday, storeTz]);

    const { data, loading, paginationLoading, pickupSlotLoading, prepLoading, error, eventNames, eventNamesLoading, eventSlug, orderDetailsError, pickupSlotsError, retryOrderDetails, retryPickupSlots, retryOverview, retryPrep } = useEventData(locationId, token, dateRange, selectedEventSlug, { orderDetailsPage, itemSalesPage, prepPage }, eventDate || undefined, pickupSlotDate || undefined);
    const handlePtrRefresh = useCallback(() => {
        retryOverview();
        retryOrderDetails();
        retryPickupSlots();
        retryPrep();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const { scrollRef: ptrScrollRef, pullDistance, isRefreshing: ptrRefreshing } = usePullToRefresh({ onRefresh: handlePtrRefresh });

    // Sync selected event when eventNames load or change
    useEffect(() => {
        if (!Array.isArray(eventNames) || eventNames.length === 0) return;
        const slug = eventNames[0]?.eventSlug ?? eventNames[0]?.slug ?? eventNames[0]?.id ?? "";
        const currentExists = eventNames.some((e) => (e.eventSlug ?? e.slug ?? e.id) === selectedEventSlug);
        if (!selectedEventSlug || !currentExists) setSelectedEventSlug(slug);
    }, [eventNames, selectedEventSlug]);

    // Reset date pickers and pages when event changes — default to today
    useEffect(() => {
        setOrderDetailsPage(1);
        setItemSalesPage(1);
        setPrepPage(1);
        setEventDate(today);
        setPickupSlotDate(today);
    }, [eventSlug, today]);

    useEffect(() => {
        setTopBarProps({
            title: "Events",
            dateFilters: undefined,
            onExport: () => {},
            onBack: () => navigate("/reports/snapshot"),
        });
    }, [setTopBarProps, navigate]);

    const kpis = data.kpis;

    const mobileOrderDetailsDatePicker = (
        <EventDatePicker
            value={eventDate || today}
            onChange={(d) => { setEventDate(d); setOrderDetailsPage(1); }}
            mobile
        />
    );

    const mobilePickupSlotDatePicker = (
        <EventDatePicker
            value={pickupSlotDate || today}
            onChange={(d) => { setPickupSlotDate(d); }}
            mobile
        />
    );

    // Drill-in routing
    if (drillIn === "prep") return <Phone><PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} /><PrepListDrillIn data={data} onBack={() => setDrillIn(null)} scrollRef={ptrScrollRef} /><BottomNav /></Phone>;
    if (drillIn === "pickup") return <Phone><PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} /><PickupSlotsDrillIn data={data} onBack={() => setDrillIn(null)} scrollRef={ptrScrollRef} /><BottomNav /></Phone>;
    if (drillIn === "customer") return <Phone><PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} /><CustomerSheetDrillIn data={data} onBack={() => setDrillIn(null)} scrollRef={ptrScrollRef} /><BottomNav /></Phone>;

    return (
        <Phone>
            <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={ptrRefreshing} />
            <TopBar variant="mobile" />
            <div ref={ptrScrollRef} style={{ flex: 1, overflowY: "auto" }}>
                {!locationId && <div style={{ padding: 24, textAlign: "center", color: T.t3, fontSize: 12 }}>Select a location to view Events.</div>}
                {locationId && (
                    <div className="rpt-flex-col rpt-gap-8" style={{ padding: 0 }}>
                        {/* Event selector: page data is driven by selected event */}
                        <EventSelector
                            eventNames={eventNames}
                            value={eventSlug}
                            onChange={setSelectedEventSlug}
                            loading={eventNamesLoading}
                            disabled={!locationId}
                            mobile
                        />
                        {loading && <EventMobileShimmer />}
                        {error && <div style={{ padding: 24, textAlign: "center", color: T.red, fontSize: 12 }}>{error}</div>}
                        {!loading && !error && (
                    <div className="rpt-flex-col rpt-gap-8" style={{ padding: "8px 16px 16px" }}>
                        {/* 1. Order Overview (combined, same container) */}
                        <EventOrderOverview orderOverview={data.orderOverview} kpis={kpis} mobile />

                        {/* 2. Order Details (compact) */}
                        <OrderDetailsTable data={data.orderDetails} mobile page={orderDetailsPage} onPageChange={setOrderDetailsPage} loading={paginationLoading} datePicker={mobileOrderDetailsDatePicker} />

                        {/* 3. Pickup by slot */}
                        <PickupSlotVolume data={data.pickupSlots} mobile datePicker={mobilePickupSlotDatePicker} loading={pickupSlotLoading} />

                        {/* 4. Item Sales */}
                        <ItemSalesTable data={data.itemSales} mobile page={itemSalesPage} onPageChange={setItemSalesPage} loading={paginationLoading} />

                        {/* 5. By Channel */}
                        <ChannelMix data={data.channelMix} mobile />

                        {/* 6. Prep Quantity List (paginated, 10 per page) */}
                        <PrepQuantityList data={data.prepQuantity} mobile page={prepPage} onPageChange={setPrepPage} loading={prepLoading} />

                        {/* Customer Run Sheet Link Card (drillable) — only show if data exists */}
                        {(data.customerRunSheet || []).length > 0 && (
                            <Card mobile drillable onClick={() => setDrillIn("customer")}>
                                <div className="rpt-flex-row rpt-flex-center rpt-gap-10">
                                    <div style={{ width: 36, height: 36, borderRadius: 10, background: T.accentSoft, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <span className="material-symbols-rounded" style={{ fontSize: 18, color: T.accent }}>list_alt</span>
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: T.t1 }}>Customer Run Sheet</div>
                                        <div style={{ fontSize: 10, color: T.t3, marginTop: 1 }}>{(data.customerRunSheet || []).length} orders \u2014 Live pickup tracking</div>
                                    </div>
                                </div>
                            </Card>
                        )}
                    </div>
                )}
                </div>
            )}
            </div>
            <BottomNav />
        </Phone>
    );
}

/* =============================================================================
   ENTRY POINT
   ============================================================================= */
export default function EventReport() {
    const isDesktop = useIsDesktop();
    useSwipeBack();
    return isDesktop ? <EventDesktopView /> : <EventMobileView />;
}
